import { CSSProperties, FC, ReactNode } from 'react'

import { abbreviateNumber } from '@hhgtech/hhg-components'
import { clsx, Skeleton } from '@mantine/core'

import { DataAttributeKey } from 'interfaces/types'

import {
  StyledCardCommunity,
  StyledCardCommunityContent,
  StyledCardCommunityFooter,
  StyledCardCommunityImageLink,
  StyledCardCommunityImageRatio,
  StyledCardCommunityImageWrap,
  StyledCardCommunityMember,
  StyledCardCommunityTitle,
  StyledCardCommunityTitleLink,
} from './CardCommunity.styled'

export type CardCommunityItemBanner = {
  id: number
  image: string
  mobile_image: string
  url: string
}
export type CardCommunityItem = {
  id: number
  permalink: string
  image: string | JSX.Element
  title: string | JSX.Element
  members_count?: number
}

export type CardCommunityItemTracking = {
  disabled?: boolean
  [DataAttributeKey: DataAttributeKey]: string | number
}

export type CardCommunityItemTrackingProps = Partial<
  Record<'title' | 'image', CardCommunityItemTracking>
>

export type CardCommunityProps = {
  loading?: boolean
  item?: CardCommunityItem
  itemTracking?: CardCommunityItemTrackingProps
  memberText?: string
  footerComponent?: ReactNode
  className?: string
  style?: CSSProperties
}
export const CardCommunity: FC<CardCommunityProps> = (props) => {
  const {
    loading,
    item,
    itemTracking,
    memberText,
    footerComponent,
    className,
    style,
  } = props
  const { permalink, image: imageProps, title, members_count = 0 } = item || {}

  const image =
    typeof imageProps === 'string' ? (
      <img src={imageProps} alt="" loading="lazy" />
    ) : (
      imageProps
    )

  if (loading) {
    return (
      <StyledCardCommunity
        className={clsx(className, 'cardCommunity')}
        style={style}
      >
        <StyledCardCommunityImageWrap className="cardCommunityImageWrap">
          <Skeleton>
            <StyledCardCommunityImageRatio
              ratio={183 / 104}
              className="cardCommunityImageRatio"
            />
          </Skeleton>
        </StyledCardCommunityImageWrap>
        <StyledCardCommunityContent className="cardCommunityContent">
          <StyledCardCommunityTitle className="cardCommunityTitle">
            <Skeleton width="100%" height="1rem" />
          </StyledCardCommunityTitle>
          <StyledCardCommunityMember className="cardCommunityMembers">
            <Skeleton width="50%" height="1rem" />
          </StyledCardCommunityMember>
          <StyledCardCommunityFooter className="cardCommunityFooter">
            <Skeleton width="100%" height="1rem" />
          </StyledCardCommunityFooter>
        </StyledCardCommunityContent>
      </StyledCardCommunity>
    )
  }

  return (
    <StyledCardCommunity
      className={clsx(className, 'cardCommunity')}
      style={style}
    >
      <StyledCardCommunityImageWrap className="cardCommunityImageWrap">
        <StyledCardCommunityImageLink
          href={permalink}
          className="cardCommunityImageLink"
          {...itemTracking?.image}
        >
          <StyledCardCommunityImageRatio
            ratio={183 / 104}
            className="cardCommunityImageRatio"
          >
            {image}
          </StyledCardCommunityImageRatio>
        </StyledCardCommunityImageLink>
      </StyledCardCommunityImageWrap>
      <StyledCardCommunityContent className="cardCommunityContent">
        <StyledCardCommunityTitleLink
          href={permalink}
          className="cardCommunityTitleLink"
          {...itemTracking?.title}
        >
          <StyledCardCommunityTitle className="cardCommunityTitle">
            {title}
          </StyledCardCommunityTitle>
        </StyledCardCommunityTitleLink>

        {!!members_count && (
          <StyledCardCommunityMember className="cardCommunityMembers">
            <span>{abbreviateNumber(members_count)}</span>&nbsp;
            {memberText}
          </StyledCardCommunityMember>
        )}
        <StyledCardCommunityFooter className="cardCommunityFooter">
          {footerComponent}
        </StyledCardCommunityFooter>
      </StyledCardCommunityContent>
    </StyledCardCommunity>
  )
}
